import React, {
  forwardRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  Check,
  Flame,
  ImageOff,
  Plus,
  SearchX,
  ShoppingBag,
} from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Componente de Grade de Produtos
 * Exibe os produtos em um grid responsivo com opção de compra
 * Suporta agrupamento por categoria quando showGrouped = true
 *
 * @param {Array} products - Array de produtos a exibir
 * @param {Array} categories - Array de categorias para agrupamento
 * @param {boolean} loading - Estado de carregamento
 * @param {string} selectedCategoryTitle - Título da categoria selecionada
 * @param {boolean} showGrouped - Se true, agrupa produtos por categoria
 * @param {function} openProductModal - Função para abrir modal do produto
 * @param {function} formatBRL - Função para formatar valores em BRL
 * @param {ref} ref - Ref para a seção (usado para scroll)
 */
export const ProductsGrid = forwardRef(
  (
    {
      products = [],
      categories = [],
      loading = false,
      selectedCategory = "",
      selectedCategoryTitle = "Produtos",
      showGrouped = false,
      openProductModal = () => {},
      formatBRL = (value) => value,
    },
    ref
  ) => {
    // Estado para forçar re-render quando localStorage mudar
    const [trackingVersion, setTrackingVersion] = useState(0);

    // Listener para sincronizar mudanças do localStorage
    useEffect(() => {
      const handleStorageChange = () => {
        setTrackingVersion((v) => v + 1);
      };

      // Escuta mudanças em ambas as chaves de rastreamento
      window.addEventListener("storage", handleStorageChange);

      // Força atualização a cada 500ms para mudanças na mesma aba
      const interval = setInterval(handleStorageChange, 500);

      return () => {
        window.removeEventListener("storage", handleStorageChange);
        clearInterval(interval);
      };
    }, []);
    /**
     * Lê o rastreamento de quantidades do localStorage
     * Retorna um mapa { [product_id]: quantidade_total }
     */
    const productsQtyTracking = useMemo(() => {
      try {
        const trackingData = localStorage.getItem("bonfim_products_tracking");
        if (trackingData) {
          return JSON.parse(trackingData);
        }
      } catch (error) {
        console.warn("⚠️ Erro ao ler rastreamento:", error);
      }
      return {};
    }, [trackingVersion]);

    /**
     * Handler otimizado para clique no card do produto
     * Usa useCallback para evitar re-criação da função em cada render
     */
    const handleProductClick = useCallback(
      (product, event) => {
        // Previne propagação se o clique foi em um elemento interativo (botão)
        if (event?.target?.closest("button")) {
          return;
        }

        // Só abre o modal se o produto estiver disponível
        if (product.available !== false) {
          openProductModal(product);
        }
      },
      [openProductModal]
    );

    /**
     * Handler específico para o botão de compra
     * Previne propagação para não duplicar a abertura do modal
     */
    const handleButtonClick = useCallback(
      (product, event) => {
        event.stopPropagation();
        if (product.available !== false) {
          openProductModal(product);
        }
      },
      [openProductModal]
    );

    /**
     * Agrupa produtos por categoria
     * Retorna um array de objetos { category: {...}, products: [...] }
     */
    const groupedProducts = useMemo(() => {
      if (!showGrouped || !products.length) {
        return null;
      }

      // Cria um mapa de categorias usando o nome como chave (normalizado)
      const categoryMap = {};
      const categoryByNormalizedName = {};

      // Adiciona categorias da API
      categories.forEach((cat) => {
        const categoryId = cat.id;
        categoryMap[categoryId] = {
          category: cat,
          products: [],
        };

        // Cria índice por nome normalizado para facilitar busca
        const normalizedName = (cat.name || "").toLowerCase().trim();
        if (normalizedName) {
          categoryByNormalizedName[normalizedName] = categoryId;
        }
      });

      // Distribui produtos nas categorias
      products.forEach((product) => {
        // O produto retorna category como string (nome ou ID da categoria)
        const productCategoryName = (product.category || "")
          .toLowerCase()
          .trim();

        // Busca a categoria pelo nome normalizado
        let categoryId = categoryByNormalizedName[productCategoryName];

        // Se não encontrou por nome, tenta buscar diretamente pelo ID
        if (!categoryId) {
          const categoryById = categories.find(
            (cat) => (cat.id || "").toLowerCase() === productCategoryName
          );
          if (categoryById) {
            categoryId = categoryById.id;
          }
        }

        // Se a categoria não existe na API, cria uma categoria dinâmica
        if (!categoryId && productCategoryName) {
          categoryId = productCategoryName;
          // Tenta encontrar a categoria na API pelo ID para pegar o nome correto
          const apiCategory = categories.find(
            (cat) => (cat.id || "").toLowerCase() === productCategoryName
          );

          categoryMap[categoryId] = {
            category: {
              id: productCategoryName,
              name: apiCategory?.name || product.category, // Usa nome da API ou fallback
              icon: apiCategory?.icon || "🏷️",
            },
            products: [],
          };
          categoryByNormalizedName[productCategoryName] = categoryId;
        }

        if (categoryId && categoryMap[categoryId]) {
          categoryMap[categoryId].products.push(product);
        }
      });

      // Filtra categorias sem produtos, remove "Festa" APENAS no modo agrupado (Todos)
      const result = Object.values(categoryMap)
        .filter((group) => {
          if (group.products.length === 0) return false;

          // Remove categoria "Festa" apenas quando está em modo agrupado (Todos os produtos)
          // Se uma categoria específica está selecionada, não filtra
          if (showGrouped) {
            const categoryName = (group.category.name || "").toLowerCase();
            if (categoryName === "festa" || categoryName === "festas")
              return false;
          }

          return true;
        })
        .sort((a, b) => {
          // Ordem customizada: Especiais primeiro, Trufados segundo, Refrescantes terceiro, demais em ordem alfabética
          const nameA = (a.category.name || "").toLowerCase();
          const nameB = (b.category.name || "").toLowerCase();

          // Prioridade 1: Especiais
          if (nameA.includes("especiai") || nameA === "especiais") return -1;
          if (nameB.includes("especiai") || nameB === "especiais") return 1;

          // Prioridade 2: Trufados
          if (nameA.includes("trufado")) return -1;
          if (nameB.includes("trufado")) return 1;

          // Prioridade 3: Refrescantes
          if (nameA.includes("refrescante")) return -1;
          if (nameB.includes("refrescante")) return 1;

          // Demais categorias em ordem alfabética (A-Z)
          return nameA.localeCompare(nameB, "pt-BR");
        });

      return result;
    }, [showGrouped, products, categories, selectedCategory]);

    /**
     * Renderiza um card de produto (layout docs/tela/DESIGN.md)
     */
    const renderProductCard = useCallback(
      (product) => {
        const isAvailable = product.available !== false;
        const qtyInCart = productsQtyTracking[product.id] || 0;
        const hasStock = product.quantity !== undefined && product.quantity !== null;
        const stockQty = Number(product.quantity ?? 0);
        const availableQty = Math.max(0, stockQty - qtyInCart);
        // Sem saldo para adicionar (addToCart bloquearia): card fica em cinza e desabilitado
        const soldOut = isAvailable && hasStock && availableQty === 0;
        const canBuy = isAvailable && !soldOut;
        const soldOutLabel = qtyInCart > 0 ? "Limite na sacola" : "Esgotado";

        return (
          <article
            key={product.id}
            onClick={(e) => canBuy && handleProductClick(product, e)}
            onKeyDown={(e) => {
              // Acessibilidade: permite abrir com Enter ou Space
              if (canBuy && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                openProductModal(product);
              }
            }}
            role="button"
            tabIndex={canBuy ? 0 : -1}
            aria-disabled={!canBuy}
            aria-label={`${product.name} - ${
              canBuy
                ? "Clique para ver detalhes"
                : soldOut
                  ? soldOutLabel
                  : "Indisponível"
            }`}
            className={cn(
              "group bg-white rounded-2xl border border-cream-300 shadow-card overflow-hidden flex flex-col h-full transition duration-300",
              canBuy
                ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-card-hover hover:border-brand-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                : "cursor-not-allowed"
            )}
          >
            {/* Imagem 1:1 com preço sobreposto */}
            <div className="relative aspect-square bg-cream-50 overflow-hidden">
              <ImageOff
                aria-hidden="true"
                className="absolute inset-0 m-auto w-8 h-8 text-cocoa-muted/40"
              />
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.name}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  className={cn(
                    "relative w-full h-full object-cover transition-transform duration-500",
                    canBuy ? "group-hover:scale-105" : "grayscale opacity-60"
                  )}
                />
              )}

              {canBuy && (
                <span className="absolute top-2 right-2 bg-brand-500 text-white px-2.5 py-1 rounded-full text-[13px] sm:text-[15px] font-extrabold leading-none shadow-md">
                  {formatBRL(product.price)}
                </span>
              )}

              {isAvailable && qtyInCart > 0 && (
                <span className="absolute top-2 left-2 bg-cocoa text-white px-2 py-1 rounded-full text-[11px] font-bold leading-none shadow-md inline-flex items-center gap-1">
                  <ShoppingBag className="w-3 h-3" aria-hidden="true" />
                  {qtyInCart}
                  <span className="sr-only">na sacola</span>
                </span>
              )}

              {!canBuy && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-cream-200 text-cocoa-muted px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide">
                    {soldOut ? soldOutLabel : "Indisponível"}
                  </span>
                </span>
              )}
            </div>

            <div className="p-3 sm:p-4 flex flex-col flex-grow gap-1.5">
              {/* Selo de estoque (já descontando o que está na sacola) */}
              {canBuy && hasStock && (
                <span
                  className={cn(
                    "self-start inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold leading-none",
                    availableQty <= 5
                      ? "bg-brand-50 text-brand-500"
                      : "bg-success-light text-success"
                  )}
                >
                  {availableQty <= 5 ? (
                    <>
                      <Flame className="w-3 h-3" aria-hidden="true" />
                      Últimas {availableQty}!
                    </>
                  ) : (
                    <>
                      <Check className="w-3 h-3" aria-hidden="true" />
                      {availableQty} disponíveis
                    </>
                  )}
                </span>
              )}

              <h3
                className={cn(
                  "font-bold text-[15px] sm:text-[17px] leading-snug tracking-tight line-clamp-2",
                  canBuy ? "text-cocoa" : "text-cocoa-muted"
                )}
              >
                {product.name}
              </h3>
              <p className="text-xs sm:text-[13px] leading-relaxed text-cocoa-muted line-clamp-2 flex-grow">
                {product.description}
              </p>

              <button
                type="button"
                disabled={!canBuy}
                onClick={(e) => handleButtonClick(product, e)}
                className={cn(
                  "mt-2 w-full min-h-[44px] rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wide inline-flex items-center justify-center gap-1.5 transition",
                  canBuy
                    ? "bg-brand-600 hover:bg-brand-700 active:scale-[0.98] text-white shadow-md shadow-brand-600/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                    : "bg-cream-200 text-cocoa-muted cursor-not-allowed"
                )}
                aria-label={`${
                  canBuy ? "Comprar" : "Produto indisponível"
                } ${product.name}`}
              >
                {canBuy ? (
                  <>
                    <Plus className="w-4 h-4" aria-hidden="true" />
                    Comprar
                  </>
                ) : soldOut ? (
                  soldOutLabel
                ) : (
                  "Indisponível"
                )}
              </button>
            </div>
          </article>
        );
      },
      [
        handleProductClick,
        handleButtonClick,
        openProductModal,
        formatBRL,
        productsQtyTracking,
      ]
    );

    /** Cabeçalho de seção: ícone + título serifado + contador */
    const renderSectionHeader = (title, icon, count) => (
      <div className="flex items-center justify-between gap-3 border-b border-cream-200 pb-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <span
            aria-hidden="true"
            className="w-9 h-9 flex-shrink-0 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center text-lg"
          >
            {icon || "🍬"}
          </span>
          <h2 className="font-serif text-xl sm:text-2xl font-bold text-cocoa tracking-tight truncate">
            {title}
          </h2>
        </div>
        <span className="flex-shrink-0 text-[11px] sm:text-xs font-bold text-cocoa bg-brand-accent/20 border border-brand-accent/40 px-3 py-1 rounded-full">
          {count} {count === 1 ? "item" : "itens"}
        </span>
      </div>
    );

    const gridClass =
      "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4";

    return (
      <section
        ref={ref}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8"
      >
        {loading ? (
          // Skeleton no formato dos cards para evitar salto de layout
          <div className={gridClass} aria-busy="true" aria-label="Carregando produtos">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-cream-300 shadow-card overflow-hidden animate-pulse"
              >
                <div className="aspect-square bg-cream-100" />
                <div className="p-3 sm:p-4 space-y-2">
                  <div className="h-4 bg-cream-100 rounded w-3/4" />
                  <div className="h-3 bg-cream-100 rounded w-full" />
                  <div className="h-11 bg-cream-100 rounded-xl mt-3" />
                </div>
              </div>
            ))}
          </div>
        ) : showGrouped && groupedProducts && groupedProducts.length > 0 ? (
          // Produtos agrupados por categoria
          <div className="space-y-10 sm:space-y-12">
            {groupedProducts.map((group) => (
              <div
                key={group.category.id}
                id={`categoria-${group.category.id}`}
                className="scroll-mt-40"
              >
                {renderSectionHeader(
                  group.category.name,
                  group.category.icon,
                  group.products.length
                )}
                <div className={gridClass}>
                  {group.products.map((product) => renderProductCard(product))}
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          // Categoria selecionada ou busca
          <>
            {renderSectionHeader(
              selectedCategoryTitle,
              categories.find(
                (c) => String(c.id) === String(selectedCategory)
              )?.icon || (selectedCategory ? "🏷️" : "🔎"),
              products.length
            )}
            <div className={gridClass}>
              {products.map((product) => renderProductCard(product))}
            </div>
          </>
        ) : (
          <div className="text-center py-16 sm:py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center">
              <SearchX className="w-8 h-8" aria-hidden="true" />
            </div>
            <h2 className="font-serif text-xl font-bold text-cocoa mb-1">
              Nenhum produto encontrado
            </h2>
            <p className="text-sm text-cocoa-muted">
              Tente outra categoria ou termo de busca
            </p>
          </div>
        )}
      </section>
    );
  }
);

ProductsGrid.displayName = "ProductsGrid";
